import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import i18n from "../i18n";
import api from "../services/api";
import type { User } from "../types";

// ============================================================
// Auth Context Types
// ============================================================

interface AuthContextValue {
  isLoggedIn: boolean;
  currentUser: User | null;
  token: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  login: (user: User, newToken: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

// Create Auth Context with proper type
const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================
// Provider Component
// ============================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("current_user");

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser) as User;
        setToken(storedToken);
        setCurrentUser(parsedUser);
        setIsLoggedIn(true);
        if (parsedUser.preferred_language) {
          i18n.changeLanguage(parsedUser.preferred_language);
        }

        // Validate token immediately
        validateTokenOnMount(storedToken);
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("current_user");
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateTokenOnMount = async (tokenToValidate: string): Promise<void> => {
    try {
      await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${tokenToValidate}` },
      });
      // Token is valid
      setIsLoading(false);
    } catch (error) {
      // Token is invalid - clear everything
      console.warn("Stored token is invalid, clearing session");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("current_user");
      setToken(null);
      setCurrentUser(null);
      setIsLoggedIn(false);
      setIsLoading(false);
    }
  };

  const login = (user: User, newToken: string): void => {
    setCurrentUser(user);
    setToken(newToken);
    setIsLoggedIn(true);
    localStorage.setItem("auth_token", newToken);
    localStorage.setItem("current_user", JSON.stringify(user));
    if (user.preferred_language) {
      i18n.changeLanguage(user.preferred_language);
    }
  };

  const logout = (): void => {
    setCurrentUser(null);
    setToken(null);
    setIsLoggedIn(false);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("current_user");
  };

  const refreshUser = async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await api.get("/auth/me");
      const userData = (response as { data?: User }).data || (response as User);
      setCurrentUser(userData);
      localStorage.setItem("current_user", JSON.stringify(userData));
      if (userData.preferred_language) {
        i18n.changeLanguage(userData.preferred_language);
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      // If token is invalid, logout
      const errorMessage = (error as Error).message || "";
      if (errorMessage.includes("401") || errorMessage.includes("Unauthorized")) {
        console.warn("Token invalid during refresh, logging out");
        logout();
      }
    }
  };

  const isAdmin: boolean = currentUser?.is_admin || false;

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        currentUser,
        token,
        isLoading,
        isAdmin,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================
// Hook to use Auth Context
// ============================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
